import { useCallback, useRef, useState } from "react";
import { Upload, Sparkles, Camera, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import ReadabilityBadge from "@/components/ReadabilityBadge";

/* Person-scoped upload widget for the Roster Wizard.
 * Extracted from RosterStage.jsx to keep component files small (<400 lines).
 */

export default function PersonUploader({ row, caseId, caseData, onDone }) {
  const inputRef = useRef(null);
  const [docType, setDocType] = useState(row.docs_expected[0]?.id || "other");
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [applyBusy, setApplyBusy] = useState(false);

  const upload = useCallback(
    async (file) => {
      if (!file) return;
      setUploading(true);
      setLastResult(null);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const params = { person_ref: row.person_ref, doc_type: docType };
        if (row.person_ref.startsWith("child:") && row.display_name) {
          params.child_full_name = row.display_name;
        }
        const { data } = await api.post(
          `/cases/${caseId}/roster/upload`,
          fd,
          { params, headers: { "Content-Type": "multipart/form-data" } },
        );
        setLastResult(data);
      } catch (err) {
        console.error("[RosterStage] upload failed:", err?.message || err);
        setLastResult({ error: err.response?.data?.detail || err.message });
      } finally {
        setUploading(false);
      }
    },
    [caseId, docType, row.person_ref, row.display_name],
  );

  const applyLast = useCallback(async () => {
    if (!lastResult?.id) return;
    setApplyBusy(true);
    try {
      await api.post(`/documents/${lastResult.id}/apply`);
      await onDone?.();
      setLastResult((prev) => (prev ? { ...prev, confirmed: true } : prev));
    } catch (err) {
      console.error("[RosterStage] apply failed:", err?.message || err);
    } finally {
      setApplyBusy(false);
    }
  }, [lastResult?.id, onDone]);

  return (
    <div className="space-y-3">
      <DocTypePicker
        personRef={row.person_ref}
        docType={docType}
        setDocType={setDocType}
        expected={row.docs_expected}
      />
      <DropZone
        personRef={row.person_ref}
        displayName={row.display_name}
        uploading={uploading}
        inputRef={inputRef}
        onFile={upload}
      />
      {lastResult && !lastResult.error && (
        <ExtractionPreview
          result={lastResult}
          onApply={applyLast}
          applyBusy={applyBusy}
          existingData={caseData.data || {}}
          personRef={row.person_ref}
        />
      )}
      {lastResult?.error && (
        <div
          className="p-3 rounded-md text-xs"
          style={{ background: "#FEF2F2", color: "#991B1B" }}
        >
          {String(lastResult.error)}
        </div>
      )}
    </div>
  );
}

function DocTypePicker({ personRef, docType, setDocType, expected }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <label style={{ color: "var(--text-secondary)" }}>What are you uploading?</label>
      <select
        className="field-input py-1 px-2 text-xs"
        value={docType}
        onChange={(e) => setDocType(e.target.value)}
        data-testid={`upload-doctype-${personRef}`}
      >
        {expected.map((d) => (
          <option key={d.id} value={d.id}>{d.label}</option>
        ))}
        <option value="other">Other supporting document</option>
      </select>
    </div>
  );
}

function DropZone({ personRef, displayName, uploading, inputRef, onFile }) {
  const cameraRef = useRef(null);
  return (
    <div
      className="dashed-drop p-5 sm:p-6 text-center cursor-pointer text-sm"
      data-testid={`upload-drop-${personRef}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
    >
      <input
        ref={inputRef}
        data-testid={`upload-input-${personRef}`}
        type="file"
        className="hidden"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
      <input
        ref={cameraRef}
        data-testid={`upload-camera-input-${personRef}`}
        type="file"
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
      <Upload className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--action)" }} />
      <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>
        {uploading ? `Reading ${displayName}'s document…` : `Snap a photo or drop a PDF`}
      </div>
      <div className="text-[11px] sm:text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
        PDF · PNG · JPEG · WEBP · up to 8 MB
      </div>
      <div className="mt-3 flex flex-col sm:flex-row gap-2 justify-center">
        <button
          type="button"
          data-testid={`upload-camera-btn-${personRef}`}
          className="btn-primary flex items-center justify-center gap-1.5 text-xs"
          style={{ padding: "0.6rem 1rem" }}
          onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
        >
          <Camera className="w-3.5 h-3.5" /> Take a photo
        </button>
        <button
          type="button"
          data-testid={`upload-choose-btn-${personRef}`}
          className="btn-ghost flex items-center justify-center gap-1.5 text-xs"
          style={{ border: "1px solid var(--border-stone)", background: "#fff", padding: "0.55rem 1rem" }}
          onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
        >
          <ImageIcon className="w-3.5 h-3.5" /> Choose file
        </button>
      </div>
    </div>
  );
}

function applyButtonLabel(confirmed, busy) {
  if (confirmed) return "Applied";
  if (busy) return "Applying…";
  return "Apply to case";
}

function ExtractionPreview({ result, onApply, applyBusy, existingData, personRef }) {
  const extracted = result.extracted || {};
  const fields = extracted.extracted_fields || {};
  const entries = Object.entries(fields).filter(([, v]) => v);
  const willWrite = previewScopedUpdates(personRef, fields, existingData);

  return (
    <div className="card-surface p-4" data-testid="roster-extract-preview">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
        <span className="font-semibold text-xs">Extracted from “{result.filename}”</span>
        <span className="pill pill-ai text-[10px]">{extracted.document_type}</span>
        <ReadabilityBadge readability={extracted.readability} compact />
      </div>
      <ReadabilityBadge readability={extracted.readability} />
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        {entries.slice(0, 8).map(([k, v]) => (
          <div key={k} className="contents">
            <dt style={{ color: "var(--text-secondary)" }}>{k.replace(/_/g, " ")}</dt>
            <dd style={{ color: "var(--text-primary)" }}>{String(v)}</dd>
          </div>
        ))}
      </dl>
      {willWrite.length > 0 && (
        <div className="mt-3 text-xs" style={{ color: "var(--text-secondary)" }}>
          Will update: <strong>{willWrite.join(", ")}</strong>
        </div>
      )}
      <button
        data-testid="roster-apply-btn"
        className="btn-primary mt-3 text-xs py-1.5"
        onClick={onApply}
        disabled={applyBusy || result.confirmed}
      >
        {applyButtonLabel(result.confirmed, applyBusy)}
      </button>
    </div>
  );
}

// Mirrors backend _compute_field_updates_scoped for a preview only. Kept small
// so the two implementations don't drift much.
const PERSON_MAP = {
  petitioner: [
    ["petitioner_first_name", "given_names"],
    ["petitioner_last_name", "surname"],
    ["petitioner_dob", "date_of_birth"],
    ["petitioner_place_of_birth", "place_of_birth"],
  ],
  beneficiary: [
    ["beneficiary_first_name", "given_names"],
    ["beneficiary_last_name", "surname"],
    ["beneficiary_dob", "date_of_birth"],
    ["beneficiary_place_of_birth", "place_of_birth"],
    ["beneficiary_nationality", "nationality"],
  ],
};

const PARENT_SUFFIX_MAP = [
  ["first_name", "given_names"],
  ["last_name", "surname"],
  ["dob", "date_of_birth"],
  ["place_of_birth", "place_of_birth"],
];

function previewScopedUpdates(personRef, fields, existingData) {
  const list = [];
  const label = (t) => t.replace(/_/g, " ");
  if (PERSON_MAP[personRef]) {
    for (const [t, s] of PERSON_MAP[personRef]) {
      if (fields[s] && !existingData[t]) list.push(label(t));
    }
    return list;
  }
  if (["petitioner_father", "petitioner_mother",
       "beneficiary_father", "beneficiary_mother"].includes(personRef)) {
    for (const [suffix, s] of PARENT_SUFFIX_MAP) {
      const t = `${personRef}_${suffix}`;
      if (fields[s] && !existingData[t]) list.push(label(t));
    }
    return list;
  }
  if (personRef.startsWith("child:")) {
    const idx = Number(personRef.split(":")[1]);
    const children = existingData.children || [];
    const child = children[idx] || {};
    for (const [target, s] of PARENT_SUFFIX_MAP) {
      if (fields[s] && !child[target]) {
        list.push(`child #${idx + 1} · ${label(target)}`);
      }
    }
  }
  return list;
}
